import React from "react";
import { useConsultantPhoto } from "@/hooks/useConsultantPhoto";
import { getAvatarColor, getInitials } from "@/lib/avatar";
import { cn } from "@/lib/utils";

const SIZES = {
  sm: "h-7 w-7 text-xs",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm",
  xl: "h-20 w-20 text-xl",
} as const;

interface ConsultantAvatarProps {
  /**
   * Consultant id for the authenticated photo lookup. Omit/null (e.g. an
   * admin user with no consultant profile) to skip the fetch and render the
   * initials fallback directly.
   */
  consultantId?: number | null;
  /**
   * Whether the consultant has a stored photo, when the payload says so
   * (`has_photo`, backend#920). `false` skips the photo request and renders
   * the initials directly; omitted means unknown, so the photo is fetched.
   */
  hasPhoto?: boolean;
  name: string;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * Consultant avatar (#776): the stored profile photo when one exists, else
 * deterministic initials. Photos are served behind auth (BE #434), so the
 * image arrives as an authenticated Blob via `useConsultantPhoto` rather
 * than a plain `<img src>` URL.
 */
export const ConsultantAvatar: React.FC<ConsultantAvatarProps> = ({
  consultantId,
  hasPhoto,
  name,
  size = "md",
  className,
}) => {
  // No id → no request, the same path as an admin without a consultant
  // profile: a known-missing photo would only come back as a 404.
  const { photoUrl } = useConsultantPhoto(hasPhoto === false ? null : consultantId);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={cn("rounded-full object-cover shrink-0", SIZES[size], className)}
      />
    );
  }

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center text-white font-semibold shrink-0",
        SIZES[size],
        getAvatarColor(name),
        className
      )}
    >
      {getInitials(name)}
    </div>
  );
};
